import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight, Download } from "lucide-react";
import { getFramework } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { SITE_URL, DEFAULT_OG_IMAGE, breadcrumbLd } from "@/lib/seo";
import { RelatedResources } from "@/components/RelatedResources";
import { useBrief } from "@/context/BriefContext";
import { track } from "@/lib/analytics";
import { ResourceGateModal } from "@/components/ResourceGateModal";

const COPPER_ACCENT = "#a0644b";

export default function FrameworkPage() {
  const { slug } = useParams();
  const reduce = useReducedMotion();
  const { openBrief } = useBrief();
  const [fw, setFw] = useState(null);
  const [status, setStatus] = useState("loading");
  const [gateOpen, setGateOpen] = useState(false);

  useEffect(() => {
    let active = true;
    setStatus("loading");
    getFramework(slug)
      .then((d) => {
        if (!active) return;
        setFw(d);
        setStatus("ready");
        track("framework_view", { slug });
      })
      .catch(() => active && setStatus("notfound"));
    return () => {
      active = false;
    };
  }, [slug]);

  const fade = {
    initial: { opacity: 0, y: reduce ? 0 : 14 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: reduce ? 0 : 0.5, ease: "easeOut" },
  };

  const service = fw?.service;

  return (
    <div className="exec-scope min-h-screen">
      <article className="exec-container pt-24 md:pt-28 pb-24">
        <div className="max-w-3xl">
          <Link to="/resources" data-testid="framework-back" className="exec-link">
            <ArrowLeft className="h-4 w-4" /> All resources
          </Link>

          {status === "loading" && (
            <div className="mt-10 animate-pulse" data-testid="framework-loading">
              <div className="h-3 w-28 bg-[color:var(--rule)]" />
              <div className="h-10 w-3/4 bg-[color:var(--rule)] mt-6" />
              <div className="h-4 w-40 bg-[color:var(--rule)] mt-8" />
              <div className="space-y-3 mt-10">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-4 w-full bg-[color:var(--rule)]" />
                ))}
              </div>
            </div>
          )}

          {status === "notfound" && (
            <div className="mt-12" data-testid="framework-notfound">
              <p className="exec-eyebrow">Framework</p>
              <h1 className="font-light text-[clamp(28px,3.6vw,42px)] leading-[1.14] mt-4">This framework isn’t available.</h1>
              <p className="exec-serif text-[18px] text-[color:var(--lead-ink)] mt-5 max-w-xl">
                It may have been moved or unpublished. The rest of the library is still here.
              </p>
              <Link to="/resources" className="exec-btn exec-btn-primary mt-7" data-testid="framework-notfound-back">
                Back to Resources <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          {status === "ready" && fw && (
            <motion.div {...fade} data-testid="framework-article">
              <Seo
                title={`${fw.title} — Copper & Vine`}
                description={fw.description || fw.title}
                path={`/resources/frameworks/${slug}`}
                type="article"
                image={DEFAULT_OG_IMAGE}
                jsonLd={[
                  {
                    "@context": "https://schema.org",
                    "@type": "Article",
                    headline: fw.title,
                    description: fw.description || undefined,
                    articleSection: fw.topic || undefined,
                    author: { "@type": "Organization", name: "Copper & Vine" },
                    publisher: { "@type": "Organization", name: "Copper & Vine", logo: { "@type": "ImageObject", url: DEFAULT_OG_IMAGE } },
                    image: DEFAULT_OG_IMAGE,
                    mainEntityOfPage: `${SITE_URL}/resources/frameworks/${slug}`,
                  },
                  breadcrumbLd([
                    { name: "Home", path: "/" },
                    { name: "Resources", path: "/resources" },
                    { name: fw.title, path: `/resources/frameworks/${slug}` },
                  ]),
                ]}
              />
              <header className="mt-9">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="exec-eyebrow">{fw.kicker || "Framework"}</p>
                  {fw.topic && (
                    <span
                      className="text-[10px] uppercase tracking-[0.14em] font-normal px-2.5 py-0.5 rounded-[2px] border border-[color:var(--copper)]/45 text-[color:var(--copper-deep)]"
                    >
                      {fw.topic}
                    </span>
                  )}
                </div>
                <h1 className="font-extralight text-[clamp(30px,4.4vw,52px)] leading-[1.1] mt-5" data-testid="framework-title">
                  {fw.title}
                </h1>
                {fw.description && (
                  <p className="exec-serif text-[clamp(18px,2vw,22px)] leading-[1.5] text-[color:var(--lead-ink)] mt-5" data-testid="framework-description">
                    {fw.description}
                  </p>
                )}
              </header>

              <div className="h-px w-16 bg-[color:var(--copper)] my-9" />

              <div className="exec-article" data-testid="framework-body">
                {(fw.body || []).map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>

              {/* Gated download */}
              <div className="mt-12 exec-card p-7 sm:p-8" style={{ borderLeft: `3px solid ${COPPER_ACCENT}` }}>
                <p className="exec-eyebrow">Take it with you</p>
                <h2 className="font-light text-[clamp(22px,2.6vw,28px)] leading-[1.18] mt-3">Get the framework as a PDF.</h2>
                <p className="exec-serif text-[16.5px] leading-[1.55] text-[color:var(--lead-ink)] mt-3 max-w-xl">
                  One email, the PDF in your inbox, nothing more.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setGateOpen(true);
                    track("framework_open_gate", { slug });
                  }}
                  data-testid="framework-download"
                  className="exec-btn exec-btn-primary mt-6"
                >
                  <Download className="h-4 w-4" /> Get the framework (PDF)
                </button>
              </div>

              <RelatedResources
                currentType="framework"
                currentSlug={fw.slug || slug}
                topic={fw.topic}
              />

              {/* Close: relevant Service + Scorecard */}
              <div className="mt-12 pt-10 border-t border-[color:var(--rule)]">
                <h2 className="font-light text-[clamp(24px,3vw,34px)] leading-[1.15]">Want this scored against your situation?</h2>
                <p className="exec-serif text-[17px] leading-[1.55] text-[color:var(--lead-ink)] mt-4 max-w-xl">
                  Score where you stand in ten questions, or see the engagement built for exactly this.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-4">
                  <Link to="/scorecard" data-testid="framework-scorecard" className="exec-btn exec-btn-primary">
                    Take the Scorecard <ArrowRight className="h-4 w-4" />
                  </Link>
                  {service?.path && (
                    <Link
                      to={service.path}
                      data-testid="framework-service-link"
                      onClick={() => track("framework_service_click", { slug, service: service.path })}
                      className="exec-btn exec-btn-secondary"
                    >
                      {service.label || "See the service"}
                    </Link>
                  )}
                  <button
                    type="button"
                    data-testid="framework-brief"
                    onClick={() => {
                      track("brief_open", { location: "framework" });
                      openBrief("general");
                    }}
                    className="exec-link"
                  >
                    Request the brief
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </article>

      <ResourceGateModal
        open={gateOpen}
        onOpenChange={setGateOpen}
        resource={fw ? { slug: fw.slug, title: fw.title, kicker: fw.kicker || "Framework" } : null}
      />
    </div>
  );
}
